import React from 'react'
import { isEmpty, toLower } from 'ramda'
import { Icon, IconSize, IconType } from 'sidecar-components/atoms/Icon'
import { TooltipButton } from 'sidecar-components/molecules/TooltipButton'

import { LearnMoreButton } from './LearnMoreButton'
import { Provider } from './PreCheckedProviders.types'

interface FooterProps {
  /** Whether the sidecar code type is Facility */
  isFacility: boolean
  /** Whether the sidecar code type is Professional */
  isProfessional: boolean
  /** Array of professional providers */
  professionalProviders: Provider[]
  /** Array of facility providers */
  facilityProviders: Provider[]
  /** Whether there are matched professional and facility providers */
  hasMatchedProfessionalAndFacilityProviders: boolean
  /** Type of component usage context */
  type?: string
  /** Pre-checked category name from Contentful */
  preCheckedCategoryName?: string
  /** Whether the component is used in Premember */
  premember?: boolean
}

export const Footer: React.FC<FooterProps> = ({
  isFacility,
  isProfessional,
  professionalProviders,
  facilityProviders,
  hasMatchedProfessionalAndFacilityProviders,
  type = '',
  preCheckedCategoryName = '',
  premember = false,
}) => {
  if (
    // if Professional, and no facility providers don't show
    // if Facility, and no professional providers don't show
    // if you get both providers, and they are the same, don't show
    (isProfessional && facilityProviders.length === 0) ||
    (isFacility && professionalProviders.length === 0) ||
    hasMatchedProfessionalAndFacilityProviders
  )
    return null

  // ----------- SubText & Tool Tip Logic -----------
  // precheckedProvidersArr shows the list of providers based on the sidecarCodeType
  const testId = `precheckedFooterTooltip_${
    isProfessional ? 'provider' : 'facility'
  }`

  const description = !isEmpty(preCheckedCategoryName)
    ? preCheckedCategoryName
    : toLower(professionalProviders[0]?.description) ||
      toLower(facilityProviders[0]?.description)

  const careSearchText = `When receiving services in the hospital related to ${description}, the pre-checked facility is ${facilityProviders[0]?.providerName}.`

  let precheckedSubTextToolTip: React.ReactNode | string = ''
  let careDetailsText = ''

  if (isProfessional) {
    careDetailsText = careSearchText

    precheckedSubTextToolTip = (
      <React.Fragment>
        <div className="mb-2">
          {facilityProviders[0]?.providerName} is a pre-checked facility
          affiliated with the above-listed pre-checked provider. If you need a
          surgery or procedure, ask for it to be scheduled at{' '}
          {facilityProviders[0]?.providerName}, the pre-checked facility related
          to {description}.
        </div>
        {!premember && <LearnMoreButton icon="forward" testId={testId} />}
      </React.Fragment>
    )
  }
  // …ask for it to be scheduled at {xyz}, the pre-checked {facility or professional} related to {care category}.
  if (isFacility) {
    careDetailsText = `When seeing a doctor for ${description} outside of the facility, the pre-checked provider is ${professionalProviders[0]?.providerName}.`

    precheckedSubTextToolTip = (
      <React.Fragment>
        <div className="mb-2">
          {professionalProviders[0]?.providerName} is a pre-checked provider
          affiliated with the above-listed pre-checked facility. If you need a
          surgery or procedure, ask {professionalProviders[0]?.providerName} to
          schedule it at {facilityProviders[0]?.providerName}.
        </div>
        {!premember && <LearnMoreButton icon="forward" testId={testId} />}
      </React.Fragment>
    )
  }

  const preCheckedProvidersSubText =
    type === 'careDetail' || type === 'episodeOfCare'
      ? careDetailsText
      : careSearchText

  return (
    <div className="flex w-full items-center" data-testid="info_footer">
      <Icon
        name={isProfessional ? 'hospital' : 'doctor'}
        type={IconType.SYSTEM}
        size={IconSize.SM}
        className="inherit mr-2 mb-1"
      />
      <p className="sh-body-sm mb-0 text-left">
        {preCheckedProvidersSubText}

        <TooltipButton
          className="mb-1 ml-2"
          testId={`btn_${testId}`}
          id={testId}
          tooltip={precheckedSubTextToolTip}
        />
      </p>
    </div>
  )
}
